import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  LinearProgress,
  Button,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import {
  ComposedChart,
  Area,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import FatigueMap from './FatigueMap';
import IntensityChart from './IntensityChart';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface AssetDetailDashboardProps {
  selectedAsset: {
    asset_id: string;
    lat: number;
    lon: number;
    fatigue_score: string | number;
  };
  detailData: {
    vibrationBreakdown: Array<{
      type: string;
      contribution: number;
      color: string;
    }>;
    tireCondition: any;
    timeline: any[];
    metrics: Array<{ type: string; value: string | number }>;
    behavior: any[];
    roadGrade?: any[];
    mapLayers?: {
      worstAreas: any[];
      roughRoad: any[];
      gripStress: any[];
      uphill: any[];
      brakeAbuse: any[];
      aggressiveCornering: any[];
    };
  };
  enableDarkTheme: boolean;
  selectedTimelineDate?: string | null;
  onTimelineDateSelect?: (date: string | null) => void;
}

const AssetDetailDashboard = ({
  selectedAsset,
  detailData,
  enableDarkTheme,
  selectedTimelineDate,
  onTimelineDateSelect,
}: AssetDetailDashboardProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedTire, setSelectedTire] = React.useState<string>('');

  React.useEffect(() => {
    if (detailData?.tireCondition?.analytics?.length) {
      const positions = Array.from(
        new Set(
          detailData.tireCondition.analytics.map((a: any) => a.tire_position)
        )
      );
      if (!positions.includes(selectedTire) && positions.length > 0) {
        setSelectedTire(positions[0] as string);
      }
    }
  }, [detailData, selectedTire]);

  const filteredAnalytics = (detailData?.tireCondition?.analytics || []).filter(
    (a: any) => a.tire_position === selectedTire
  );

  // Local Map Toggle handled by sections now
  const [roadMapLayer, setRoadMapLayer] = React.useState<string>('roughRoad');
  const lastSelectedDateRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (selectedTimelineDate && detailData?.mapLayers) {
      // ONLY trigger auto-switch if the DATE has actually changed (new drill-down)
      if (selectedTimelineDate !== lastSelectedDateRef.current) {
        lastSelectedDateRef.current = selectedTimelineDate;

        const layers = ['roughRoad', 'gripStress', 'uphill'];
        const mapLayers = detailData.mapLayers as any;
        if ((mapLayers[roadMapLayer]?.length || 0) === 0) {
          const firstWithData = layers.find(
            (l) => (mapLayers[l]?.length ?? 0) > 0
          );
          if (firstWithData) setRoadMapLayer(firstWithData);
        }
      }
    }
  }, [selectedTimelineDate, detailData, roadMapLayer]);

  // Process timeline data for rolling average and markers
  const processedTimeline = React.useMemo(() => {
    if (!detailData?.timeline?.length) return [];

    // Sort and clean
    const sorted = [...detailData.timeline].sort((a, b) =>
      dayjs(a.date).diff(dayjs(b.date))
    );

    return sorted.map((item, index, arr) => {
      // 7-day rolling average of 'peak'
      const window = arr.slice(Math.max(0, index - 6), index + 1);
      const rollingAvg =
        window.reduce(
          (sum: number, curr: any) => sum + (Number(curr.peak) || 0),
          0
        ) / window.length;

      // Parse reason_pct and cycle_stats if string
      let reasons: any = {};
      let cycleStats: any = {};
      try {
        reasons =
          typeof item.reason_pct === 'string'
            ? JSON.parse(item.reason_pct)
            : item.reason_pct || {};
        cycleStats =
          typeof item.cycle_stats === 'string'
            ? JSON.parse(item.cycle_stats)
            : item.cycle_stats || {};
      } catch (e) {
        /* ignore */
      }

      // Identify leading cause for marker
      const sortedReasons = Object.entries(reasons).sort(
        (a: any, b: any) => (Number(b[1]) || 0) - (Number(a[1]) || 0)
      );
      const leadingCause =
        sortedReasons.length > 0 ? sortedReasons[0][0] : null;

      // Event type mapping for markers
      let markerColor = '#8b8e98';
      let markerShape: 'circle' | 'triangle' = 'circle';

      if (
        leadingCause?.includes('Brake') ??
        leadingCause?.includes('Cornering')
      ) {
        markerColor = '#ff4d4f';
      } else if (
        leadingCause?.includes('Roughness') ??
        leadingCause?.includes('Vibration')
      ) {
        markerColor = '#fa8c16';
        markerShape = 'triangle';
      } else if (
        leadingCause?.includes('Inflation') ??
        leadingCause?.includes('Tire')
      ) {
        markerColor = '#1890ff';
      } else if (leadingCause?.includes('Uphill')) {
        markerColor = '#722ed1';
      }

      const showMarker =
        Number(item.peak) > 11 || Number(item.peak) > rollingAvg * 1.2;
      const finalMarkerColor = Number(item.peak) > 11 ? '#ff4d4f' : markerColor;

      return {
        ...item,
        peak: Number(item.peak) || 0,
        ffi: Number(item.ffi) || 0,
        rollingAvg,
        reasons,
        cycleStats,
        displayDate: dayjs(item.date).format('DD-MMM'),
        leadingCause: leadingCause ?? 'Unknown',
        markerColor: finalMarkerColor,
        markerShape,
        showMarker,
      };
    });
  }, [detailData?.timeline]);

  const CustomTooltip = ({
    active,
    payload,
    onTimelineDateSelect,
    selectedTimelineDate,
  }: any) => {
    const { t } = useTranslation();
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isSelected = selectedTimelineDate === data.date;

      return (
        <Box
          sx={{
            backgroundColor: enableDarkTheme ? '#1E232E' : '#fff',
            border: `1px solid ${
              isSelected
                ? '#3666EC'
                : enableDarkTheme
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.1)'
            }`,
            borderRadius: '8px',
            p: 2,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onTimelineDateSelect?.(isSelected ? null : data.date);
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '13px',
                fontWeight: 800,
              }}
            >
              {dayjs(data.date).format('DD MMM')}
            </Typography>
            {data.peak > 11 && (
              <Box
                sx={{
                  backgroundColor: 'rgba(255,77,79,0.1)',
                  px: 1,
                  py: 0.2,
                  borderRadius: '4px',
                  border: '1px solid rgba(255,77,79,0.3)',
                }}
              >
                <Typography
                  sx={{
                    color: '#ff4d4f',
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('critical_fatigue_day')}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              color: enableDarkTheme ? '#fff' : '#000',
              fontSize: '11px',
              mb: 1,
            }}
          >
            {t('possible_fatigue_causes')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Tire Condition Group */}
            {(() => {
              const targetDate = dayjs(data.date).format('YYYY-MM-DD');
              const dayAnalytics = (
                detailData?.tireCondition?.analytics || []
              ).filter(
                (a: any) => dayjs(a.date).format('YYYY-MM-DD') === targetDate
              );
              return (
                dayAnalytics.some(
                  (a: any) =>
                    (Number(a.under_pct) || 0) > 0.05 ||
                    (Number(a.over_pct) || 0) > 0.05
                ) ||
                Object.entries(data.reasons || {}).some(
                  ([k]) => k.includes('Inflation') || k.includes('Tire')
                )
              );
            })() && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: '#597ef7',
                    fontSize: '9px',
                    fontWeight: 700,
                    minWidth: 80,
                  }}
                >
                  {t('tire_condition')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(() => {
                    const targetDate = dayjs(data.date).format('YYYY-MM-DD');
                    const dayAnalytics = (
                      detailData?.tireCondition?.analytics || []
                    ).filter(
                      (a: any) =>
                        dayjs(a.date).format('YYYY-MM-DD') === targetDate
                    );

                    const tireMapping: Record<string, string> = {
                      'Front-Left': 'FL',
                      'Front-Right': 'FR',
                      'Rear-Outside-Left': 'ROL',
                      'Rear-Outside-Right': 'ROR',
                      'Rear-Inside-Left': 'RIL',
                      'Rear-Inside-Right': 'RIR',
                    };

                    const underInflatedTires = dayAnalytics
                      .filter((a: any) => (Number(a.under_pct) || 0) > 0.05)
                      .map((a: any) => {
                        const pos = a.tire_position || '';
                        return tireMapping[pos] || pos;
                      });
                    const overInflatedTires = dayAnalytics
                      .filter((a: any) => (Number(a.over_pct) || 0) > 0.05)
                      .map((a: any) => {
                        const pos = a.tire_position || '';
                        return tireMapping[pos] || pos;
                      });

                    const labels: string[] = [];
                    if (underInflatedTires.length > 0) {
                      labels.push(
                        t('critical_asset_under_inflation_list', {
                          tires: underInflatedTires.join(', '),
                        })
                      );
                    }
                    if (overInflatedTires.length > 0) {
                      labels.push(
                        t('critical_asset_over_inflation_list', {
                          tires: overInflatedTires.join(', '),
                        })
                      );
                    }

                    // If no date-exact match, aggregate across all analytics to show affected positions
                    // Use > 0 threshold to handle both decimal (0–1) and percentage (0–100) storage formats
                    if (labels.length === 0) {
                      const allAnalytics =
                        detailData?.tireCondition?.analytics || [];
                      const underPositions = Array.from(
                        new Set(
                          allAnalytics
                            .filter(
                              (a: any) => (Number(a.under_pct) || 0) > 0.05
                            )
                            .map(
                              (a: any) =>
                                tireMapping[a.tire_position || ''] ||
                                a.tire_position ||
                                ''
                            )
                            .filter(Boolean)
                        )
                      );
                      const overPositions = Array.from(
                        new Set(
                          allAnalytics
                            .filter(
                              (a: any) => (Number(a.over_pct) || 0) > 0.05
                            )
                            .map(
                              (a: any) =>
                                tireMapping[a.tire_position || ''] ||
                                a.tire_position ||
                                ''
                            )
                            .filter(Boolean)
                        )
                      );
                      if (underPositions.length > 0)
                        labels.push(
                          t('critical_asset_under_inflation_list', {
                            tires: underPositions.join(', '),
                          })
                        );
                      if (overPositions.length > 0)
                        labels.push(
                          t('critical_asset_over_inflation_list', {
                            tires: overPositions.join(', '),
                          })
                        );
                    }

                    // Final fallback to reason keys only (no threshold — show all inflation/tire causes)
                    if (labels.length === 0) {
                      Object.entries(data.reasons || {}).forEach(([k]) => {
                        if (k.includes('Inflation') || k.includes('Tire')) {
                          labels.push(k);
                        }
                      });
                    }

                    return labels.map((label) => (
                      <Box
                        key={label}
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: '4px',
                          border: '1px solid rgba(89,126,247,0.2)',
                          backgroundColor: 'rgba(89,126,247,0.05)',
                        }}
                      >
                        <Typography sx={{ color: '#597ef7', fontSize: '8px' }}>
                          {label}
                        </Typography>
                      </Box>
                    ));
                  })()}
                </Box>
              </Box>
            )}

            {/* Driver Behaviour Group */}
            {Object.entries(data.reasons || {}).some(
              ([k]) => k.includes('Brake') || k.includes('Cornering')
            ) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: '#ff4d4f',
                    fontSize: '9px',
                    fontWeight: 700,
                    minWidth: 80,
                  }}
                >
                  {t('driver_behaviour')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(data.reasons || {})
                    .filter(
                      ([k]) => k.includes('Brake') || k.includes('Cornering')
                    )
                    .map(([c]) => (
                      <Box
                        key={c}
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: '4px',
                          border: '1px solid rgba(255,102,102,0.2)',
                          backgroundColor: 'rgba(255,102,102,0.05)',
                        }}
                      >
                        <Typography sx={{ color: '#ff6666', fontSize: '8px' }}>
                          {c}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            {/* Road Condition Group */}
            {Object.entries(data.reasons || {}).some(
              ([k]) =>
                k.includes('Roughness') ||
                k.includes('Uphill') ||
                k.includes('Traction')
            ) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: '#fa8c16',
                    fontSize: '9px',
                    fontWeight: 700,
                    minWidth: 80,
                  }}
                >
                  {t('road_condition')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(data.reasons || {})
                    .filter(
                      ([k]) =>
                        k.includes('Roughness') ||
                        k.includes('Uphill') ||
                        k.includes('Traction')
                    )
                    .map(([c]) => (
                      <Box
                        key={c}
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: '4px',
                          border: '1px solid rgba(250,140,22,0.2)',
                          backgroundColor: 'rgba(250,140,22,0.05)',
                        }}
                      >
                        <Typography sx={{ color: '#fa8c16', fontSize: '8px' }}>
                          {c}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mt: 2,
              borderTop: '1px solid rgba(255,255,255,0.05)',
              pt: 1.5,
            }}
          >
            {[
              {
                label: t('loaded_cycles'),
                value: data.cycleStats?.loaded_cycles,
              },
              {
                label: t('empty_cycles'),
                value: data.cycleStats?.empty_cycles,
              },
              {
                label: t('high_fatigue_cycles'),
                value: data.cycleStats?.fatigue_cycles,
                color: '#ff4d4f',
              },
              {
                label: t('payload_transferred'),
                value: data.cycleStats?.payload_total_ton
                  ? `${Number(
                      data.cycleStats.payload_total_ton
                    ).toLocaleString()}t`
                  : null,
              },
              {
                label: t('critical_asset_avg_speed'),
                value: data.cycleStats?.avg_speed_kph
                  ? `${Number(data.cycleStats.avg_speed_kph).toFixed(1)} km/h`
                  : null,
              },
              {
                label: t('engine_hrs'),
                value: data.cycleStats?.engine_hrs
                  ? `${Number(data.cycleStats.engine_hrs).toFixed(1)} h`
                  : null,
              },
            ].map(
              (stat, i) =>
                stat.value != null && (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ color: '#8b8e98', fontSize: '10px' }}>
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        color:
                          stat.color ?? (enableDarkTheme ? '#fff' : '#000'),
                        fontSize: '10px',
                        fontWeight: 700,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                )
            )}
          </Box>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{ color: palette.primary, fontSize: '10px', fontWeight: 700 }}
            >
              {isSelected
                ? t('critical_asset_click_to_clear_filter')
                : t('critical_asset_click_to_filter_map')}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  if (!selectedAsset) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ccc',
        }}
      >
        {t('select_an_asset_to_view_details')}
      </Box>
    );
  }

  // Calculate vibrationData from diagnostic reasons (more accurate than physical AXIS fields)
  const aggregatedReasons = (detailData?.timeline || []).reduce<
    Record<string, number>
  >((acc, day) => {
    let reasons: Record<string, number> = {};
    try {
      reasons =
        typeof day.reason_pct === 'string'
          ? JSON.parse(day.reason_pct)
          : day.reason_pct || {};
    } catch {
      reasons = {};
    }
    Object.keys(reasons).forEach((key) => {
      acc[key] = (Number(acc[key]) || 0) + (Number(reasons[key]) || 0);
    });
    return acc;
  }, {});

  const totalReasonPct = Object.values(aggregatedReasons).reduce(
    (sum, v) => sum + (Number(v) || 0),
    0
  );
  const getReasonPct = (keywords: string[]) => {
    const sum = Object.entries(aggregatedReasons)
      .filter(([k]) => keywords.some((kw) => k.toLowerCase().includes(kw)))
      .reduce((s, [, v]) => s + (Number(v) || 0), 0);
    return totalReasonPct > 0 ? Math.round((sum / totalReasonPct) * 100) : 0;
  };

  const vibrationData = [
    {
      type: t('critical_asset_vertical_vibration'),
      contribution: 48,
      color: '#fa6216ff',
    },
    {
      type: t('critical_asset_longitudinal_vibration'),
      contribution: 34,
      color: '#fabd14',
    },
    {
      type: t('critical_asset_roll_imbalance'),
      contribution: 18,
      color: '#722ed1',
    },
  ];

  const totalVibration = 100;

  const ffiTrendText = t('critical_asset_ffi_trend_static');
  const ffiTrendColor = '#fa6216ff';
  const ffeiPerHourLabel = t('critical_asset_ffei_per_hour');

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 4,
        backgroundColor: enableDarkTheme ? '#0c0e14' : '#f5f7fa',
        overflowY: 'auto',
      }}
    >
      {/* 1. HEADER & ANALYSIS SIDEBAR GRID */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={3.5}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontWeight: 700,
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              {t('how_is')} {selectedAsset.asset_id} {t('being_damaged')}
            </Typography>
            <Typography sx={{ color: '#ccc', fontSize: '12px' }}>
              {vibrationData[0]?.contribution > vibrationData[1]?.contribution
                ? t('critical_asset_vertical_shock_damage')
                : t('critical_asset_longitudinal_stress_damage')}
            </Typography>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              backgroundColor: enableDarkTheme ? '#151921' : '#fff',
              borderRadius: '8px',
              border: enableDarkTheme
                ? '1px solid rgba(255,255,255,0.05)'
                : '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '11px',
                fontWeight: 700,
                mb: 1.5,
                textTransform: 'uppercase',
              }}
            >
              {selectedAsset.asset_id} {t('frame_fatigue_analysis')}
            </Typography>
            <Typography
              sx={{
                color: ffiTrendColor,
                fontSize: '13px',
                fontWeight: 700,
                mb: 2,
              }}
            >
              {ffiTrendText}
            </Typography>

            <Box sx={{ mb: 3 }}>
              {[
                {
                  label: t('rough_road_contributed'),
                  count: detailData?.mapLayers?.roughRoad?.length ?? 0,
                  unit: t('critical_asset_hotspots'),
                  pct: getReasonPct(['rough', 'roughness', 'road roughness']),
                },
                {
                  label: t('aggressive_cornering_contributed'),
                  count:
                    detailData?.mapLayers?.aggressiveCornering?.length ?? 0,
                  unit: t('critical_asset_events'),
                  pct: getReasonPct(['cornering', 'aggressive']),
                },
                {
                  label: t('uphill_route_contributed'),
                  count: detailData?.mapLayers?.uphill?.length ?? 0,
                  unit: t('critical_asset_hotspots'),
                  pct: getReasonPct(['uphill']),
                },
              ].map((factor, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: factor.pct > 5 ? '#ff4d4f' : '#52c41a',
                    }}
                  >
                    {factor.pct > 5 ? '⚠' : '✓'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: enableDarkTheme ? '#ccc' : '#333',
                    }}
                  >
                    {factor.label} {factor.pct > 0 ? `${factor.pct}%` : ''}
                    {factor.count > 0
                      ? ` : ${factor.count} ${factor.unit}`
                      : ''}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              sx={{
                color: '#ccc',
                fontSize: '10px',
                fontWeight: 700,
                mb: 2,
                textTransform: 'uppercase',
              }}
            >
              {t('ffi_breakdown_vibration_sources')}
            </Typography>
            {vibrationData.map((v: any, i: number) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: enableDarkTheme ? '#ccc' : '#333',
                    }}
                  >
                    {v.type}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: enableDarkTheme ? '#fff' : '#000',
                      fontWeight: 700,
                    }}
                  >
                    {((v.contribution / totalVibration) * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(v.contribution / totalVibration) * 100}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: v.color || '#fa8c16',
                    },
                  }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8.5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {t('1_fatigue_timeline_with_rolling_trend')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedTimelineDate && (
                <Button
                  size="small"
                  onClick={() => onTimelineDateSelect?.(null)}
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: palette.primary,
                    border: '1px solid #3666EC',
                    borderRadius: '12px',
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    minWidth: 'auto',
                    '&:hover': { backgroundColor: 'rgba(54,102,236,0.1)' },
                  }}
                >
                  {t('reset_view')}
                </Button>
              )}
              <Box
                sx={{
                  backgroundColor: '#E6A23C',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '4px',
                }}
              >
                <Typography
                  sx={{
                    color: enableDarkTheme ? '#fff' : '#000',
                    fontSize: '12px',
                    fontWeight: 800,
                  }}
                >
                  FFI{' '}
                  {processedTimeline.length > 0
                    ? processedTimeline[
                        processedTimeline.length - 1
                      ].peak.toFixed(1)
                    : '9.6'}{' '}
                  {t('critical')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {Object.values(
              processedTimeline
                .filter((t) => t.showMarker)
                .reduce<Record<string, any>>(
                  (acc: Record<string, any>, t: any) => {
                    const key = t.leadingCause;
                    if (!acc[key] || t.peak > acc[key].peak) {
                      acc[key] = {
                        ...t,
                        count: (Number(acc[key]?.count) || 0) + 1,
                      };
                    } else {
                      acc[key].count = (Number(acc[key].count) || 0) + 1;
                    }
                    return acc;
                  },
                  {}
                )
            )
              .sort((a: any, b: any) => b.peak - a.peak)
              .slice(0, 8)
              .map((item: any, idx: number) => (
                <Box
                  key={idx}
                  onClick={() => {
                    onTimelineDateSelect?.(item.date);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '4px',
                    border: `1px solid ${item.markerColor}44`,
                    backgroundColor: `${item.markerColor}11`,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: `${item.markerColor}22` },
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius:
                        item.markerShape === 'triangle' ? '0' : '50%',
                      backgroundColor: item.markerColor,
                      clipPath:
                        item.markerShape === 'triangle'
                          ? 'polygon(50% 0%, 0% 100%, 100% 100%)'
                          : 'none',
                    }}
                  />
                  <Typography
                    sx={{
                      color: enableDarkTheme ? '#fff' : '#000',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}
                  >
                    {item.leadingCause} ({item.count}
                    {t('x')} {dayjs(item.date).format('D-MMM')} Peak
                  </Typography>
                </Box>
              ))}
          </Box>

          <Paper
            sx={{
              p: 3,
              backgroundColor: enableDarkTheme ? '#11151D' : '#fff',
              height: 320,
              borderRadius: '8px',
              border: enableDarkTheme ? 'none' : '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart
                data={processedTimeline}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                onClick={(e: any) => {
                  if (e?.activePayload) {
                    const clickedDate = e.activePayload[0].payload.date;
                    onTimelineDateSelect?.(
                      clickedDate === selectedTimelineDate ? null : clickedDate
                    );
                  }
                }}
              >
                <defs>
                  <linearGradient id="colorFfi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3666EC" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3666EC" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPeak" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fa8c16" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#fa8c16" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={
                    enableDarkTheme
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.05)'
                  }
                />
                <XAxis
                  dataKey="date"
                  hide={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#4E5A6B', fontSize: 10 }}
                />
                <YAxis
                  yAxisId="left"
                  hide={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#4E5A6B', fontSize: 10 }}
                  domain={[0, 'auto']}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: '#2eb37f', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 'auto']}
                  label={{
                    value: t('critical_asset_cumulative_ffei'),
                    angle: 90,
                    position: 'insideRight',
                    fill: '#2eb37f',
                    fontSize: 10,
                    dy: -40,
                  }}
                />
                <Tooltip
                  cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 2 }}
                  content={
                    <CustomTooltip
                      enableDarkTheme={enableDarkTheme}
                      onTimelineDateSelect={onTimelineDateSelect}
                      selectedTimelineDate={selectedTimelineDate}
                    />
                  }
                />
                <Legend verticalAlign="top" height={36} />

                {selectedTimelineDate && (
                  <ReferenceLine
                    yAxisId="left"
                    x={selectedTimelineDate}
                    stroke={palette.primary}
                    strokeWidth={2}
                    label={{
                      value: t('critical_asset_selected'),
                      position: 'top',
                      fill: palette.primary,
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  />
                )}

                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="ffi"
                  name={t('critical_asset_ffei_trend')}
                  stroke={palette.primary}
                  strokeWidth={2}
                  fill="url(#colorFfi)"
                  activeDot={{ r: 4, fill: palette.primary, strokeWidth: 0 }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="peak"
                  name={t('critical_asset_peak_fatigue')}
                  stroke="#fa8c16"
                  strokeWidth={2}
                  fill="url(#colorPeak)"
                  activeDot={{ r: 4, fill: '#fa8c16', strokeWidth: 0 }}
                />

                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="rollingAvg"
                  name={t('critical_asset_rolling_avg')}
                  stroke={enableDarkTheme ? '#fff' : '#888'}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative_ffi"
                  name={t('critical_asset_cumulative_fatigue')}
                  stroke="#2eb37f"
                  strokeWidth={3}
                  dot={false}
                />

                <Scatter
                  yAxisId="left"
                  dataKey="peak"
                  name={t('critical_asset_events')}
                  shape={(props: any) => {
                    const { cx, cy, payload } = props;
                    if (!payload.showMarker) return null;

                    if (payload.markerShape === 'triangle') {
                      return (
                        <path
                          key={`marker-${payload.date}`}
                          d={`M ${Number(cx)} ${Number(cy) - 6} L ${
                            Number(cx) - 6
                          } ${Number(cy) + 6} L ${Number(cx) + 6} ${
                            Number(cy) + 6
                          } Z`}
                          fill={payload.markerColor}
                        />
                      );
                    }
                    return (
                      <circle
                        key={`marker-${payload.date}`}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={payload.markerColor}
                      />
                    );
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {selectedTimelineDate && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(54, 102, 236, 0.1)',
                  border: '1px solid #3666EC',
                  borderRadius: '16px',
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    color: enableDarkTheme ? '#fff' : '#000',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                >
                  {t('filtered')} {dayjs(selectedTimelineDate).format('D MMM')}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    onTimelineDateSelect?.(null);
                  }}
                  sx={{
                    minWidth: 'auto',
                    p: 0,
                    ml: 1,
                    color: enableDarkTheme ? '#fff' : '#000',
                    opacity: 0.7,
                    '&:hover': { opacity: 1, backgroundColor: 'transparent' },
                    fontSize: '10px',
                    fontWeight: 800,
                  }}
                >
                  ✕
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 2. OPERATIONAL CONTRIBUTORS SECTION */}
      <Box sx={{ mb: 4, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            sx={{
              color: enableDarkTheme ? '#fff' : '#000',
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {t('operational_contributors')}
          </Typography>
          <Typography sx={{ color: '#ccc', fontSize: '11px' }}>
            {t('why_these_stresses_are_happening')}
          </Typography>
          <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#ccc' }} />
        </Box>

        {/* ROAD CONDITION SUB-SECTION */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {t('road_condition_2')}
            </Typography>
            {!selectedTimelineDate && (
              <Box sx={{ ml: 'auto' }}>
                <Select
                  value={roadMapLayer}
                  onChange={(e) => {
                    setRoadMapLayer(e.target.value);
                  }}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '10px',
                    color: enableDarkTheme ? '#fff' : '#000',
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.05)'
                      : '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <MenuItem value="roughRoad" sx={{ fontSize: '10px' }}>
                    {t('rough_road_patches')}
                  </MenuItem>
                  <MenuItem value="gripStress" sx={{ fontSize: '10px' }}>
                    {t('slippery_road_patches')}
                  </MenuItem>
                  <MenuItem value="uphill" sx={{ fontSize: '10px' }}>
                    {t('uphill_fatigue_segments')}
                  </MenuItem>
                </Select>
              </Box>
            )}
          </Box>
          <Grid container spacing={3}>
            {!selectedTimelineDate ? (
              <>
                {((detailData?.mapLayers as any)?.[roadMapLayer]?.length ?? 0) >
                  0 && (
                  <Grid item xs={12} md={7}>
                    <Paper
                      sx={{
                        height: 280,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <FatigueMap
                        hotspots={
                          (detailData?.mapLayers as any)?.[roadMapLayer] ?? []
                        }
                        height="100%"
                        center={{
                          lat: selectedAsset.lat,
                          lon: selectedAsset.lon,
                        }}
                        isMiniMap
                        enableDarkTheme={enableDarkTheme}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          left: 10,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          p: 0.5,
                          borderRadius: '4px',
                        }}
                      >
                        <Typography sx={{ fontSize: '10px', color: '#fff' }}>
                          ▶{' '}
                          {roadMapLayer === 'gripStress'
                            ? t('critical_asset_traction_loss')
                            : roadMapLayer === 'roughRoad'
                            ? t('critical_asset_road_roughness')
                            : roadMapLayer === 'uphill'
                            ? t('critical_asset_uphill_segment')
                            : roadMapLayer
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())}{' '}
                          –{' '}
                          {t('critical_asset_hotspots_suffix', {
                            count:
                              (detailData?.mapLayers as any)?.[roadMapLayer]
                                ?.length ?? 0,
                          })}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  md={
                    ((detailData?.mapLayers as any)?.[roadMapLayer]?.length ??
                      0) > 0
                      ? 5
                      : 12
                  }
                >
                  <Box sx={{ height: 280 }}>
                    <IntensityChart
                      data={detailData?.roadGrade ?? []}
                      enableDarkTheme={true}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              (() => {
                const activeLayers = (
                  ['roughRoad', 'gripStress', 'uphill'] as const
                ).filter(
                  (layer) =>
                    ((detailData?.mapLayers as any)?.[layer]?.length ?? 0) > 0
                );
                return activeLayers.map((layer) => (
                  <Grid
                    item
                    xs={12}
                    md={
                      activeLayers.length === 1
                        ? 12
                        : activeLayers.length === 2
                        ? 6
                        : 4
                    }
                    key={layer}
                  >
                    <Paper
                      sx={{
                        height: 280,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      <FatigueMap
                        hotspots={(detailData?.mapLayers as any)?.[layer] ?? []}
                        height="100%"
                        center={{
                          lat: selectedAsset.lat,
                          lon: selectedAsset.lon,
                        }}
                        isMiniMap
                        enableDarkTheme={enableDarkTheme}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          left: 10,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          p: 0.5,
                          borderRadius: '4px',
                        }}
                      >
                        <Typography sx={{ fontSize: '10px', color: '#fff' }}>
                          ▶{' '}
                          {layer === 'gripStress'
                            ? t('critical_asset_traction_loss')
                            : layer === 'roughRoad'
                            ? t('critical_asset_road_roughness')
                            : layer === 'uphill'
                            ? t('critical_asset_uphill_segment')
                            : (layer as string)
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())}{' '}
                          –{' '}
                          {t('critical_asset_hotspots_suffix', {
                            count:
                              (detailData?.mapLayers as any)?.[layer]?.length ??
                              0,
                          })}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ));
              })()
            )}
          </Grid>
        </Box>

        {/* DRIVER BEHAVIOUR SUB-SECTION */}
        {(!selectedTimelineDate ||
          (detailData?.mapLayers?.brakeAbuse?.length ?? 0) > 0 ||
          (detailData?.mapLayers?.aggressiveCornering?.length ?? 0) > 0) && (
          <Box sx={{ mb: 6 }}>
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '12px',
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              {t('driver_behavior')}
            </Typography>
            <Grid container spacing={3}>
              {(() => {
                const activeBehaviorLayers = (
                  ['brakeAbuse', 'aggressiveCornering'] as const
                ).filter(
                  (layer) =>
                    ((detailData?.mapLayers as any)?.[layer]?.length ?? 0) > 0
                );

                return (
                  <>
                    {(!selectedTimelineDate ||
                      (detailData?.mapLayers?.brakeAbuse?.length ?? 0) > 0) && (
                      <Grid
                        item
                        xs={12}
                        md={
                          !selectedTimelineDate ||
                          activeBehaviorLayers.length > 1
                            ? 6
                            : 12
                        }
                      >
                        <Paper
                          sx={{
                            height: 320,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            position: 'relative',
                            border: '1px solid rgba(255,255,255,0.05)',
                          }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              position: 'absolute',
                              top: 0,
                              width: '100%',
                              zIndex: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#ccc',
                                fontWeight: 700,
                              }}
                            >
                              {t('brake_abuse')} {selectedAsset.asset_id}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#ff4d4f',
                                fontWeight: 700,
                                backgroundColor: 'rgba(255,77,79,0.1)',
                                px: 0.5,
                              }}
                            >
                              {(detailData?.mapLayers?.brakeAbuse?.length ??
                                0) > 0
                                ? t('critical_asset_severity_high')
                                : t('critical_asset_severity_none')}
                            </Typography>
                          </Box>
                          <FatigueMap
                            hotspots={detailData?.mapLayers?.brakeAbuse ?? []}
                            height="100%"
                            center={{
                              lat: selectedAsset.lat,
                              lon: selectedAsset.lon,
                            }}
                            isMiniMap
                            enableDarkTheme={enableDarkTheme}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              left: 10,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              p: 0.5,
                              borderRadius: '4px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#fff',
                              }}
                            >
                              {t('dump_point_grade_descents')}{' '}
                              {t('critical_asset_events_count_period', {
                                count:
                                  detailData?.mapLayers?.brakeAbuse?.length ??
                                  0,
                                period: selectedTimelineDate
                                  ? t('critical_asset_on_this_day')
                                  : t('critical_asset_today'),
                              })}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    {(!selectedTimelineDate ||
                      (detailData?.mapLayers?.aggressiveCornering?.length ??
                        0) > 0) && (
                      <Grid
                        item
                        xs={12}
                        md={
                          !selectedTimelineDate ||
                          activeBehaviorLayers.length > 1
                            ? 6
                            : 12
                        }
                      >
                        <Paper
                          sx={{
                            height: 320,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            position: 'relative',
                            border: '1px solid rgba(255,255,255,0.05)',
                          }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              position: 'absolute',
                              top: 0,
                              width: '100%',
                              zIndex: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#ccc',
                                fontWeight: 700,
                              }}
                            >
                              {t('aggressive_cornering')}{' '}
                              {selectedAsset.asset_id}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#fff',
                                fontWeight: 700,
                                backgroundColor: '#ff4d4f',
                                px: 0.5,
                              }}
                            >
                              {t('critical_asset_severity_critical')}
                            </Typography>
                          </Box>
                          <FatigueMap
                            hotspots={
                              detailData?.mapLayers?.aggressiveCornering ?? []
                            }
                            height="100%"
                            center={{
                              lat: selectedAsset.lat,
                              lon: selectedAsset.lon,
                            }}
                            isMiniMap
                            enableDarkTheme={enableDarkTheme}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              left: 10,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              p: 0.5,
                              borderRadius: '4px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#fff',
                              }}
                            >
                              {t('hauling_loading_intersection_stress')}{' '}
                              {detailData?.mapLayers?.aggressiveCornering
                                ?.length ?? 0}{' '}
                              {t('hotspots_detected')}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </>
                );
              })()}
            </Grid>
          </Box>
        )}

        {/* TIRE CONDITION SUB-SECTION */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {t('tire_condition_2')}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{ border: '1px solid #ff4d4f', px: 1, borderRadius: '2px' }}
            >
              <Typography
                sx={{ fontSize: '9px', color: '#ff4d4f', fontWeight: 800 }}
              >
                &#9888; {t('high_impact')}
              </Typography>
            </Box>
          </Box>

          <Paper
            sx={{
              p: 3,
              backgroundColor: enableDarkTheme ? 'rgba(0,0,0,0.4)' : '#fff',
              borderRadius: '8px',
              border: enableDarkTheme
                ? '1px solid rgba(255,255,255,0.05)'
                : '1px solid rgba(0,0,0,0.1)',
              height: 450,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 4,
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: '14px',
                  color: enableDarkTheme ? '#fff' : '#000',
                }}
              >
                {selectedTire || t('critical_asset_no_tire_detected')}{' '}
                {t('daily_tire_condition_distribution_vs_ffei_hour')}
              </Typography>
              <Box sx={{ position: 'absolute', right: 0 }}>
                <Select
                  value={selectedTire}
                  onChange={(e) => {
                    setSelectedTire(e.target.value);
                  }}
                  size="small"
                  displayEmpty
                  sx={{
                    height: '32px',
                    fontSize: '12px',
                    minWidth: '120px',
                    color: enableDarkTheme ? '#fff' : '#000',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary,
                    },
                  }}
                >
                  {Array.from(
                    new Set(
                      (detailData?.tireCondition?.analytics || []).map(
                        (a: any) => a.tire_position
                      )
                    )
                  ).map((pos: any) => (
                    <MenuItem key={pos} value={pos} sx={{ fontSize: '12px' }}>
                      {pos}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            {filteredAnalytics.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={filteredAnalytics}
                  margin={{ top: 20, right: 30, bottom: 20, left: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={true}
                    horizontal={true}
                    stroke={
                      enableDarkTheme
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.05)'
                    }
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: enableDarkTheme ? '#8b8e98' : '#666',
                      fontSize: 10,
                    }}
                    tickFormatter={(val) => dayjs(val).format('YYYY-MM-DD')}
                    axisLine={{ stroke: enableDarkTheme ? '#333' : '#eee' }}
                    tickMargin={10}
                  ></XAxis>
                  <YAxis
                    yAxisId="left"
                    tick={{
                      fill: enableDarkTheme ? '#8b8e98' : '#666',
                      fontSize: 10,
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                    label={{
                      value: t('critical_asset_pct_readings'),
                      angle: -90,
                      position: 'insideLeft',
                      fill: enableDarkTheme ? '#8b8e98' : '#666',
                      fontSize: 11,
                      dy: 40,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: palette.primary, fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    domain={['auto', 'auto']}
                    label={{
                      value: ffeiPerHourLabel,
                      angle: 90,
                      position: 'insideRight',
                      fill: palette.primary,
                      fontSize: 11,
                      dy: -40,
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: enableDarkTheme ? '#1a1a1a' : '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      color: enableDarkTheme ? '#fff' : '#000',
                      fontSize: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    }}
                    labelFormatter={(val) => dayjs(val).format('YYYY-MM-DD')}
                    formatter={(value: any, name: string) => {
                      if (name === ffeiPerHourLabel)
                        return [Number(value).toFixed(0), name];
                      return [`${(Number(value) * 100).toFixed(1)}%`, name];
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="left"
                    iconType="square"
                    wrapperStyle={{
                      fontSize: 11,
                      color: enableDarkTheme ? '#8b8e98' : '#666',
                      paddingBottom: 20,
                    }}
                  />

                  {detailData?.tireCondition?.analytics?.map(
                    (entry: any, index: number) => {
                      if (entry.is_high_ffei) {
                        return (
                          <ReferenceArea
                            key={`ref-${index}`}
                            x1={entry.date}
                            x2={entry.date}
                            yAxisId="left"
                            fill="#E9F2F9"
                            fillOpacity={enableDarkTheme ? 0.1 : 0.5}
                          />
                        );
                      }
                      return null;
                    }
                  )}

                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="under_pct"
                    stackId="1"
                    fill="#F37C72"
                    stroke="none"
                    name={t('critical_asset_under_inflated')}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="over_pct"
                    stackId="1"
                    fill="#A97CBF"
                    stroke="none"
                    name={t('critical_asset_over_inflated')}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="high_temp_pct"
                    stackId="1"
                    fill="#C66567"
                    stroke="none"
                    name={t('critical_asset_high_temp')}
                  />

                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="ffei_per_hour"
                    stroke={palette.primary}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                    name={ffeiPerHourLabel}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                  fontSize: '12px',
                  fontStyle: 'italic',
                }}
              >
                {t(
                  'no_tire_distribution_data_available_for_the_selected_asset_a'
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* FOOTER INSIGHT BAR */}
      <Box
        sx={{
          mt: 2,
          backgroundColor: enableDarkTheme ? '#151921' : '#f5f7fa',
          borderRadius: '8px',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: enableDarkTheme
            ? '1px solid rgba(255,255,255,0.05)'
            : '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          sx={{ color: enableDarkTheme ? '#fff' : '#000', fontSize: '12px' }}
        >
          <span style={{ color: '#fa8c16', fontWeight: 700 }}>{t('next')}</span>{' '}
          {selectedAsset.asset_id} {t('at_ffi')}{' '}
          {Number(selectedAsset.fatigue_score).toFixed(1)}{' '}
          {t('is_accumulating_fatigue_fleet_operations_are_within_safe_par')}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            navigate('/frame-fatigue/overview');
          }}
          sx={{
            color: enableDarkTheme ? '#fff' : '#000',
            borderColor: enableDarkTheme
              ? 'rgba(255,255,255,0.2)'
              : 'rgba(0,0,0,0.2)',
            fontSize: '11px',
            textTransform: 'none',
          }}
        >
          {t('back_to_fleet_overview')}
        </Button>
      </Box>
    </Box>
  );
};

export default AssetDetailDashboard;
